import React, { useEffect, useRef, useState } from "react";
import markerIcon from "../../../assets/icon_map_locate.svg"; // 내 위치 마커 이미지 경로
import markerNbIcon from "../../../assets/icon_map_nb.svg"; // 노브랜드 마커
import markerEdIcon from "../../../assets/icon_map_ed.svg"; // 노브랜드 마커
import markerSelectedIcon from "../../../assets/icon_map_selected.svg"; // 선택된 마커 이미지 경로
import markerEDSelectedIcon from "../../../assets/icon_map_ed_selected.svg"; // 선택된 마커 이미지 경로

const { REACT_APP_NAVER_MAP_CLIENT_ID } = process.env;

const NaverMap = ({
  brandId,
  branch,
  selectBranch,
  branchList,
  setKeyword,
  location,
  setLocation,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const selectedMarkerRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const locatingRef = useRef(false);

  const loadScript = (url) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  /**
   * branchList 데이터 유효성 검증
   * @returns {boolean} 데이터가 유효한 경우 true
   */
  const isValidBranchList = () => {
    // branchList가 존재하고 배열인지 확인
    if (!branchList || !Array.isArray(branchList)) {
      console.warn(
        "branchList is not valid:",
        { branchList, type: typeof branchList }
      );
      return false;
    }

    // branchList가 비어있는지 확인
    if (branchList.length === 0) {
      console.warn("branchList is empty");
      return false;
    }

    // 각 지점 데이터가 유효한지 확인
    const hasValidBranches = branchList.every((branch) => {
      const hasRequiredFields =
        branch &&
        typeof branch === "object" &&
        branch.id !== undefined &&
        branch.id !== null &&
        branch.map_x !== undefined &&
        branch.map_x !== null &&
        branch.map_y !== undefined &&
        branch.map_y !== null;

      if (!hasRequiredFields) {
        console.warn("Invalid branch data:", branch);
      }

      return hasRequiredFields;
    });

    return hasValidBranches;
  };

  /**
   * 지도 마커 초기화
   * branchList의 유효성을 먼저 확인하고 마커를 생성
   */
  const initializeMap = () => {
    // Step 1: 위치 정보 확인
    if (location.lat1 === 0 && location.lng1 === 0) {
      console.warn("Location is not available (lat1, lng1 = 0)");
      return;
    }

    // Step 2: 지도 인스턴스 생성 또는 업데이트
    if (!mapInstance.current) {
      mapInstance.current = new window.naver.maps.Map(mapRef.current, {
        center: new window.naver.maps.LatLng(location.lat1, location.lng1),
        zoom: 15,
        minZoom: 10,
        zoomControl: false,
        mapTypeControl: false,
        logoControl: false,
        mapDataControl: false,
      });
    } else {
      mapInstance.current.setCenter(
        new window.naver.maps.LatLng(location.lat1, location.lng1)
      );
    }

    // Step 3: 기존 마커 제거
    markersRef.current.forEach((marker) => {
      try {
        marker.setMap(null);
      } catch (error) {
        console.error("Error removing marker:", error);
      }
    });
    markersRef.current = [];

    // Step 4: 내 위치 마커 제거
    if (currentLocationMarkerRef.current) {
      try {
        currentLocationMarkerRef.current.setMap(null);
      } catch (error) {
        console.error("Error removing current location marker:", error);
      }
    }

    // Step 5: 현재 내 위치 마커 표시
    try {
      currentLocationMarkerRef.current = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(location.lat1, location.lng1),
        map: mapInstance.current,
        icon: {
          url: markerIcon,
          size: new window.naver.maps.Size(37, 29),
          scaledSize: new window.naver.maps.Size(37, 29),
        },
        zIndex: 999,
      });
    } catch (error) {
      console.error("Error creating current location marker:", error);
    }

    // Step 6: branchList 유효성 확인
    if (!isValidBranchList()) {
      console.warn("Cannot create branch markers: invalid branchList");
      markersRef.current = [];
      return;
    }

    // Step 7: 지점 마커 생성
    try {
      markersRef.current = branchList
        .map((marker) => {
          // 각 마커의 필수 데이터 재확인
          if (!marker || !marker.id || marker.map_x === undefined || marker.map_y === undefined) {
            console.warn("Skipping invalid marker:", marker);
            return null;
          }

          try {
            const markerInstance = new window.naver.maps.Marker({
              position: new window.naver.maps.LatLng(marker.map_x, marker.map_y),
              map: mapInstance.current,
              icon: {
                url: brandId === "NB" ? markerNbIcon : markerEdIcon,
                size: new window.naver.maps.Size(37, 29),
                scaledSize: new window.naver.maps.Size(37, 29),
              },
            });

            // 마커 클릭 이벤트 추가
            window.naver.maps.Event.addListener(markerInstance, "click", () => {
              // selectBranch 함수 호출 전 id 검증
              if (marker.id !== undefined && marker.id !== null) {
                selectBranch(marker.id);
              } else {
                console.error("Cannot select branch: marker.id is invalid");
              }
            });

            return markerInstance;
          } catch (error) {
            console.error("Error creating marker instance:", error, marker);
            return null;
          }
        })
        .filter((marker) => marker !== null); // null 마커 필터링

      console.log(`Successfully created ${markersRef.current.length} markers`);
    } catch (error) {
      console.error("Error creating markers:", error);
      markersRef.current = [];
    }
  };

  useEffect(() => {
    loadScript(
      `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${REACT_APP_NAVER_MAP_CLIENT_ID}&submodules=geocoder`
    )
      .then(() => {
        const checkNaverMapsLoaded = () => {
          if (
            window.naver &&
            window.naver.maps &&
            window.naver.maps.Service?.reverseGeocode
          ) {
            getCurrentLocation();
          } else {
            setTimeout(checkNaverMapsLoaded, 100);
          }
        };

        checkNaverMapsLoaded();
      })
      .catch((error) => {
        console.error("Failed to load Naver Map script:", error);
      });
  }, []);

  useEffect(() => {
    if (location.lat1 !== 0 && location.lng1 !== 0 && window.naver?.maps) {
      initializeMap();
    }
  }, [location]);

  useEffect(() => {
    if (branch?.id && markersRef.current.length > 0) {
      const selectedBranch = branchList?.find((b) => b.id === branch.id);

      if (!selectedBranch) {
        console.warn(
          "Selected branch not found in branchList:",
          branch.id,
          branchList
        );
        return;
      }

      try {
        const newCenter = new window.naver.maps.LatLng(
          selectedBranch.map_x,
          selectedBranch.map_y
        );
        mapInstance.current.panTo(newCenter);

        // 마커 아이콘 변경
        markersRef.current.forEach((marker) => {
          if (marker.getPosition().equals(newCenter)) {
            marker.setIcon({
              url: brandId === "NB" ? markerSelectedIcon : markerEDSelectedIcon,
              size: new window.naver.maps.Size(37, 29),
              scaledSize: new window.naver.maps.Size(37, 29),
            });
            selectedMarkerRef.current = marker;
          } else {
            marker.setIcon({
              url: brandId === "NB" ? markerNbIcon : markerEdIcon,
              size: new window.naver.maps.Size(37, 29),
              scaledSize: new window.naver.maps.Size(37, 29),
            });
          }
        });
      } catch (error) {
        console.error("Error updating marker selection:", error);
      }
    }
  }, [branch, branchList]);

  useEffect(() => {
    // branchList가 업데이트될 때마다 마커 재생성
    if (
      location.lat1 !== 0 &&
      location.lng1 !== 0 &&
      window.naver?.maps &&
      isValidBranchList()
    ) {
      initializeMap();
    }
  }, [branchList]);

  const getCityNameFromCoordinates = ({ lat1, lng1 }) => {
    const latlng = new window.naver.maps.LatLng(lat1, lng1);
    window.naver.maps.Service.reverseGeocode(
      {
        coords: latlng,
        orders: [
          window.naver.maps.Service.OrderType.ADDR,
          window.naver.maps.Service.OrderType.ROAD_ADDR,
        ].join(","),
      },
      function (status, response) {
        if (status === window.naver.maps.Service.Status.ERROR) {
          return console.error("Geocoding failed: " + status);
        }

        const items = response.v2.results;
        if (items.length > 0) {
          const address1 = items[0].region.area1.name;
          const address2 = items[0].region.area2.name;
          setKeyword(`${address1} ${address2}`);
        }
      }
    );
  };

  const moveMapTo = (lat1, lng1) => {
    if (!mapInstance.current || !window.naver?.maps) return;
    mapInstance.current.setCenter(new window.naver.maps.LatLng(lat1, lng1));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported.");
      return;
    }
    // 이전 요청이 끝나기 전 클릭은 무시한다. getCurrentPosition은 취소 API가 없고
    // timeout 기본값이 Infinity라, 연타하면 콜백과 역지오코딩이 나중에 한꺼번에 실행된다.
    if (locatingRef.current) return;

    if (location.lat1 !== 0 && location.lng1 !== 0) {
      moveMapTo(location.lat1, location.lng1);
    }

    locatingRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locatingRef.current = false;
        const lat1 = position.coords.latitude;
        const lng1 = position.coords.longitude;
        if (location.lat1 === lat1 && location.lng1 === lng1) {
          moveMapTo(lat1, lng1);
          return;
        }
        setLocation({ lat1, lng1 });
        getCityNameFromCoordinates({ lat1, lng1 });
      },
      () => {
        locatingRef.current = false;
        if (location.lat1 !== 0 && location.lng1 !== 0) return;
        setLocation({ lat1: 37.5666103, lng1: 126.9783882 });
        getCityNameFromCoordinates({ lat1: 37.5666103, lng1: 126.9783882 });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 10000 }
    );
  };

  return (
    <div className="relative flex-1 w-full bg-slate-400">
      <div id="map" ref={mapRef} className="w-full h-full"></div>
      <button
        onClick={getCurrentLocation}
        className="absolute p-2 text-sm text-white bg-blue-500 rounded top-2 right-2"
      >
        현재 위치 찾기
      </button>
    </div>
  );
};

export default NaverMap;
